import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import RoleApi from "../api/manageRoleApi";
import { toast } from "react-toastify";
import { setFetchRoleData } from "../../../../core/data/redux/authSlice";
import { useDispatch } from "react-redux";
import { handleModalAction } from "../../../../utils/helpers/handleModalAction";
import { useFormik } from "formik";
import * as Yup from "yup";

const EditRoleModal = ({ role }) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const roleNameArr = JSON.parse(localStorage.getItem("roleNameArr")) || [];

  // Use useRef to store previous role data
  const prevRoleRef = useRef(role);

  const mutationUpdateRole = useMutation({
    mutationFn: RoleApi.updateRole,
    onSuccess: () => {
      queryClient.invalidateQueries(["roles"]);
      dispatch(setFetchRoleData());
      toast.success("Role updated successfully.");
      formik.resetForm();
      handleModalAction("edit_role", "hide");
    },
    onError: (error) => {
      toast.error(error.response?.data?.msg || "Failed to update role.");
    },
  });

  const formik = useFormik({
    initialValues: {
      id: role?._id || "",
      name: role?.name || "",
      parentRoleId: role?.parentRole?._id || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      parentRoleId: Yup.string().required("Reports to is required."),
    }),
    onSubmit: (values) => {
      const prevValues = {
        id: prevRoleRef.current?._id || "",
        name: prevRoleRef.current?.name || "",
        parentRoleId: prevRoleRef.current?.parentRole?._id || "",
      };

      if (JSON.stringify(values) === JSON.stringify(prevValues)) {
        toast.warning("No changes detected.");
        return;
      }

      const updatedRoleData = {
        name: values.name,
        parentRole: values.parentRoleId || null,
      };
      mutationUpdateRole.mutate({ id: values.id, updatedRoleData });
    },
  });

  // Update prevRoleRef whenever role updates
  useEffect(() => {
    if (role) {
      prevRoleRef.current = role;
    }
  }, [role]);

  return (
    <div className="modal fade" id="edit_role" role="dialog">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Edit Role</h5>
            <button
              className="btn-close custom-btn-close border p-1 me-0 text-dark"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>

          <form onSubmit={formik.handleSubmit}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="col-form-label">Role Name</label>
                <div className="input-group">
                  <input
                    className="form-control text-capitalize"
                    name="name"
                    value={formik.values.name.split("-").join(" ")}
                    disabled
                  />
                  <div className="input-group-text">
                    <i className="ti ti-lock" />
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <label className="col-form-label">
                  Reports To<span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <select
                    id="parentRoleId"
                    className="form-select"
                    name="parentRoleId"
                    value={formik.values.parentRoleId}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <option value="" disabled>
                      -None-
                    </option>
                    {roleNameArr?.map((role, index) => (
                      <option key={index} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                  <div className="input-group-text">
                    <i className="ti ti-pencil" />
                  </div>
                </div>
                {formik.touched.parentRoleId && formik.errors.parentRoleId ? (
                  <div className="text-danger">
                    {formik.errors.parentRoleId}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="modal-footer">
              <div className="d-flex align-items-center justify-content-end m-0">
                <Link
                  to="#"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={mutationUpdateRole.isPending}
                >
                  {mutationUpdateRole.isPending ? "Updating..." : "Update"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditRoleModal;
